import { useEffect, useRef, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, LogOut, MessageCircleQuestion, Moon, Search, Settings, User, X, Layers } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import SiteLogo from "./SiteLogo";
import { COMMUNITY_LINKS, NAV_SECTIONS, type ActiveNav } from "./navLinks";
import { ICON_BUTTON, MENU_TOGGLE } from "./navStyles";

/**
 * The phone and tablet menu (under 1024px): a full-screen dialog drawn over the page.
 *
 * - Its top row repeats the navbar's (logo, search, then Close where Menu was, same size), so opening it reads as
 *   the Menu button turning into Close. Close keeps its colours on hover and after a tap.
 * - The five sections are rows, each with its own links indented under it, then the communities, then Ask and the
 *   theme switch, and Sign in last as a plain row.
 * - It fades in and out (state change only), keeps Tab inside itself, closes on Escape, on any link and on a route
 *   change (Navbar), and hands focus back to the Menu button when it closes.
 * - It is portalled to <body>: the navbar is a fixed element, and a fixed child inside it would be laid out
 *   against the navbar, not the screen.
 */

const ROW = "flex w-full items-center gap-3 rounded-control px-3 text-left type-body transition-colors duration-150 hover:bg-secondary";
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface MobileMenuProps {
  open: boolean;
  active: ActiveNav | null;
  pathname: string;
  onClose: () => void;
  /** Called once the closing fade has finished (the navbar opens the search sheet then, if asked). */
  onExitComplete?: () => void;
  onSearch: () => void;
  onAsk: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  signedInEmail: string | null;
  signInHref: string;
  onSignOut: () => void;
  /** The navbar's Menu button: focus goes back to it when the menu closes. */
  returnFocusRef: RefObject<HTMLButtonElement>;
}

export default function MobileMenu(props: MobileMenuProps) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <AnimatePresence onExitComplete={props.onExitComplete}>{props.open && <MenuDialog key="menu" {...props} />}</AnimatePresence>,
    document.body,
  );
}

function MenuDialog({
  active,
  pathname,
  onClose,
  onSearch,
  onAsk,
  theme,
  onToggleTheme,
  signedInEmail,
  signInHref,
  onSignOut,
  returnFocusRef,
}: MobileMenuProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closeLatest = useRef(onClose);
  closeLatest.current = onClose;

  // Open: lock the page behind and move focus to Close. While open, the keys are watched on the whole document, so
  // they still work after a tap on empty space has moved focus to <body>: Escape closes, and Tab and Shift+Tab stay
  // inside the dialog (rows that are not shown, like Ask where the chat is not on the page, are skipped). Close:
  // unlock and give focus back to the Menu button, unless something else has taken it in the meantime (Ask opens
  // the chat, which focuses its own input).
  useEffect(() => {
    const dialog = dialogRef.current;
    const trigger = returnFocusRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeLatest.current();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const focused = document.activeElement;
      if (!focused || !dialog.contains(focused)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && focused === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && focused === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      const focused = document.activeElement;
      if (!focused || focused === document.body || dialog?.contains(focused)) trigger?.focus();
    };
  }, [returnFocusRef]);

  // "page" for the link that is this page, "true" for the one the page sits under (Compounds on a compound page).
  const currentFor = (href: string) => (active?.href === href ? (pathname === href ? "page" : "true") : undefined);
  const isDark = theme === "dark";

  return (
    <motion.div
      ref={dialogRef}
      id="mobile-nav-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Site menu"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.2, ease: "easeOut" } }}
      exit={{ opacity: 0, transition: { duration: 0.15, ease: "easeIn" } }}
      className="fixed inset-0 z-modal flex flex-col bg-background"
    >
      <div className="shrink-0 border-b border-border">
        <div className="container mx-auto flex h-14 items-center px-4">
          <SiteLogo onClick={onClose} />
          <div className="ml-auto flex items-center gap-1">
            <button type="button" onClick={onSearch} aria-label="Search" className={ICON_BUTTON}>
              <Search className="h-5 w-5" aria-hidden="true" />
            </button>
            <button ref={closeRef} type="button" onClick={onClose} className={MENU_TOGGLE}>
              <X className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="max-[359px]:sr-only">Close</span>
            </button>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ y: -8 }}
        animate={{ y: 0, transition: { duration: 0.2, ease: "easeOut" } }}
        exit={{ y: -8, transition: { duration: 0.15, ease: "easeIn" } }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        <div className="container mx-auto px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-3">
          <nav aria-label="Site sections">
            <ul className="space-y-1">
              {NAV_SECTIONS.map((section) => {
                const Icon = section.icon;
                const current = currentFor(section.href);
                return (
                  <li key={section.id}>
                    <Link
                      to={section.href}
                      onClick={onClose}
                      aria-current={current}
                      className={cn(ROW, "min-h-12 font-semibold text-foreground", current && "bg-secondary")}
                    >
                      <Icon className={cn("h-5 w-5 shrink-0", current ? "text-foreground" : "text-muted-foreground")} aria-hidden="true" />
                      {section.label}
                    </Link>
                    {section.links?.length ? (
                      // The rule sits under the section's icon; the links' text lines up with the section label.
                      <ul className="ml-[1.375rem] border-l border-border">
                        {section.links.map((link) => {
                          const linkCurrent = currentFor(link.href);
                          return (
                            <li key={link.href}>
                              <Link
                                to={link.href}
                                onClick={onClose}
                                aria-current={linkCurrent}
                                className={cn(
                                  "flex min-h-11 items-center rounded-r-control pl-[1.3125rem] pr-3 type-body transition-colors duration-150 hover:bg-secondary hover:text-foreground",
                                  linkCurrent ? "bg-secondary font-medium text-foreground" : "text-muted-foreground",
                                )}
                              >
                                {link.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            <div className="mt-4 border-t border-border pt-4">
              <h2 id="mobile-nav-community" className="type-label px-3 pb-1 text-muted-foreground">
                Community
              </h2>
              <ul aria-labelledby="mobile-nav-community">
                {COMMUNITY_LINKS.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} target="_blank" rel="noopener noreferrer" onClick={onClose} className={cn(ROW, "min-h-11 text-foreground")}>
                      <span className="min-w-0 flex-1 truncate">{link.label}</span>
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="mt-4 space-y-1 border-t border-border pt-4">
            {/* Shown only where the chat is on the page (FloatingAIChat sets data-chat on <html>). */}
            <button type="button" onClick={onAsk} className={cn(ROW, "hidden min-h-12 text-foreground [html[data-chat]_&]:flex")}>
              <MessageCircleQuestion className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              Ask BodyHackGuide
            </button>
            {/* The whole row is the switch's label, so a tap anywhere on it flips the theme. */}
            <label className={cn(ROW, "min-h-12 cursor-pointer text-foreground")}>
              <Moon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="flex-1">Dark theme</span>
              <Switch checked={isDark} onCheckedChange={onToggleTheme} />
            </label>
          </div>

          <div className="mt-4 border-t border-border pt-4">
            {signedInEmail ? (
              <>
                <p className="truncate px-3 pb-1 text-sm text-muted-foreground">{signedInEmail}</p>
                <ul className="space-y-1">
                  <li>
                    <Link to="/settings" onClick={onClose} className={cn(ROW, "min-h-12 text-foreground")}>
                      <Settings className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      Account settings
                    </Link>
                  </li>
                  <li>
                    <Link to="/stack-builder" onClick={onClose} className={cn(ROW, "min-h-12 text-foreground")}>
                      <Layers className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      My stacks
                    </Link>
                  </li>
                  <li>
                    <button type="button" onClick={onSignOut} className={cn(ROW, "min-h-12 text-foreground")}>
                      <LogOut className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      Sign out
                    </button>
                  </li>
                </ul>
              </>
            ) : (
              <Link to={signInHref} onClick={onClose} className={cn(ROW, "min-h-12 text-foreground")}>
                <User className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                Sign in
              </Link>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
